// src/Components/Drawer/CustomDrawer/DrawerService.js

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { storage, db } from '../../../Firebase/firebase';

export const DrawerService = {
  /**
   * Uploads photos to Storage and updates Firestore
   */
  savePhotos: async (selectedMarker, photoItems, photoDescriptions) => {
    if (!selectedMarker || !selectedMarker.id)
      return { updatedItemsState: [], finalPhotoData: [] };

    const groupId = selectedMarker.markerGroupID || selectedMarker.id;

    const finalPhotoData = [];
    const updatedItemsState = [];

    for (const item of photoItems) {
      let downloadUrl = item.img;

      if (item.file instanceof File) {
        const uniqueName = `${item.id}_${item.file.name}`;
        const storageRef = ref(storage, `photos/${groupId}/${uniqueName}`);
        await uploadBytes(storageRef, item.file);
        downloadUrl = await getDownloadURL(storageRef);

        if (item.img.startsWith('blob:')) URL.revokeObjectURL(item.img);
      }

      // AMENDED: Safely extract and infer true media type to prevent file-type defaults causing UI fallbacks
      let inferredType = item.type || 'image';
      if (item.file && item.file.type) {
        if (item.file.type.startsWith('video/')) inferredType = 'video';
        else if (item.file.type.startsWith('audio/')) inferredType = 'audio';
      }
      if (inferredType === 'image' && downloadUrl) {
        if (downloadUrl.match(/\.(mp4|mov|webm|avi|mkv|wmv|flv|mts|m4v)/i))
          inferredType = 'video';
        else if (downloadUrl.match(/\.(mp3|wav|ogg|aac)/i)) inferredType = 'audio';
      }

      const photoObj = {
        id: item.id,
        url: downloadUrl,
        description: photoDescriptions[item.id] || '',
        fileName: item.title || 'Untitled',
        uploadedAt: new Date().toISOString(),
        type: inferredType,
      };
      finalPhotoData.push(photoObj);

      updatedItemsState.push({
        id: item.id,
        img: downloadUrl,
        title: item.title,
        file: null,
        type: inferredType,
      });
    }

    const docRef = doc(db, 'markerGroups', groupId);
    // AMENDED: Switched to setDoc with merge: true to avoid failures on empty docs
    await setDoc(
      docRef,
      {
        photos: finalPhotoData,
        lastModified: new Date().toISOString(),
      },
      { merge: true },
    );

    return { updatedItemsState, finalPhotoData };
  },

  /**
   * Uploads documents to Storage and updates Firestore
   */
  saveDocs: async (selectedMarker, docItems) => {
    if (!selectedMarker || !selectedMarker.id)
      return { updatedItemsState: [], finalDocData: [] };

    const groupId = selectedMarker.markerGroupID || selectedMarker.id;

    const finalDocData = [];
    const updatedItemsState = [];

    for (const item of docItems) {
      let downloadUrl = item.url;

      if (item.fileObject instanceof File) {
        const uniqueName = `${item.id}_${item.fileName.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const storageRef = ref(storage, `documents/${groupId}/${uniqueName}`);
        await uploadBytes(storageRef, item.fileObject);
        downloadUrl = await getDownloadURL(storageRef);
      }

      const docObj = {
        id: item.id,
        url: downloadUrl,
        fileName: item.fileName,
        fileSize: item.fileSize,
        fileType: item.fileType,
        timestamp: item.timestamp || new Date().toISOString(),
      };
      finalDocData.push(docObj);

      updatedItemsState.push({
        ...item,
        url: downloadUrl,
        fileObject: null,
      });
    }

    const docRef = doc(db, 'markerGroups', groupId);
    // AMENDED: Switched to setDoc with merge: true to avoid failures on empty docs
    await setDoc(
      docRef,
      {
        documents: finalDocData,
        lastModified: new Date().toISOString(),
      },
      { merge: true },
    );

    return { updatedItemsState, finalDocData };
  },

  /**
   * Uploads a single Marker Hover Image
   */
  uploadMarkerImage: async (groupId, file) => {
    if (!file || !(file instanceof File)) return null;

    const idForPath = groupId || Date.now().toString();
    const uniqueFilename = `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `marker_images/${idForPath}/${uniqueFilename}`);

    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  },

  /**
   * Deletes a specific file from Firebase Storage using its URL
   */
  deleteFile: async (fileUrl) => {
    if (!fileUrl || !fileUrl.includes('firebasestorage')) return;
    try {
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);
    } catch (err) {
      if (err.code !== 'storage/object-not-found') {
        console.warn('Failed to delete orphaned file:', err);
      }
    }
  },
};
